import React, {
    Component
} from 'react';

import {Button} from 'primereact/button';
import {Panel} from 'primereact/panel';

import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import Item, {ItemAttributesForCreation} from '~/shared/src/ci/Item';

import ItemTypeDropdown from '~/frontend/src/components/base/shared/field/ItemTypeDropdown'

import {XhrComponentState} from '~/frontend/src/reducers/xhr';
import User from '~/shared/src/model/User';
import _, {throttle} from 'lodash';
import ReferenceSetDropdown from '~/frontend/src/components/base/shared/field/ReferenceSetDropdown';
import {ReferenceAttributesForCreation} from '~/shared/src/ci/Reference';
import SearchItemDropdown from '~/frontend/src/components/base/shared/field/SearchItemDropdown';
import {
    checkCardinality,
    checkCardinalityFeOnly
} from '~/frontend/src/utilities/dataUtils';
import {getItemTypeDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {se} from 'suneditor/src/lang';


const defaultProps = {
    /**
     * The ItemTypeName of the Item to add.
     */
    itemTypeName: '' as string,

    /**
     * Relevant reference sets
     */
    includeReferenceSets: [] as number[],

    /**
     * Add a new item, an existing one or a clone of an existing (this becomes also a new item)
     */
    addMode: 'new' as 'new' | 'existing' | 'clone',

    /**
     * Should the add button be enabled.
     */
    allowAdd: false as boolean,

    useThisLabel: true as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;
    dataContext : string;

    /**
     * The database schema of the Item to add.
     */
    schema : Schema;

    targetItemId? : number;
    targetItemTypeName? : string;

    cloneItemId? : number;
    cloneItemTypeName? : string;
    cloneItemLabel? : string;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Callback to invoke if addButton was clicked.
     *
     * @param {ItemAttributesForCreation} item
     */
    onAddNode : ( item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) => void;

    onInteraction? : () => void;

    onHide : () => void;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    allowedReferenceSetIds? : { [ referenceSetId : number ] : 'dest' | 'src' };
};

type StateProps = {

    /**
     * The running requests belonging to this component (uid).
     *
     * Will be mapped from global state.
     */
    xhrRequestsForComponent? : XhrComponentState;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};


export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps;

type State = {
    itemTypeName : string;
    selectedItemTypeName : string;
    selectedItemTypeLabel : string;
    possibleItemTypes : number[];
    selectedItem? : Item;
    selectedReferenceSetId : number;
    possibleReferenceSets : number[];
    lastItemTypeName : string;
    statusId : number;
    cardinalityError : string;
    cardinalityCheck : boolean;
};

const defaultState = {
    itemTypeName          : '',
    selectedItemTypeName  : '',
    selectedItemTypeLabel : '',
    possibleItemTypes     : [],
    selectedReferenceSetId: 0,
    possibleReferenceSets : [],
    lastItemTypeName      : '',
    cardinalityError      : '',
    cardinalityCheck      : false
};

const onCreateButtonClick = ( props : AllProps, state : State ) => {
    const {
              schema,
              dataContext,
              targetItemId,
              targetItemTypeName,
              cloneItemId,
              addMode,
              allowedReferenceSetIds
          }              = props,
          addExisting    = addMode === 'existing',
          clone          = addMode === 'clone',
          {
              selectedItemTypeName,
              selectedReferenceSetId,
              selectedItem,
              statusId
          }              = state,
          referenceSetId = selectedReferenceSetId < 0 ? -selectedReferenceSetId : selectedReferenceSetId;

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    if ( ( addExisting && selectedItem ) || selectedItemTypeName !== '' ) {
        const itemTypeId = cloneItemId ? global.data[ dataContext ]?.items[ cloneItemId ]?.itemTypeId : undefined;

        let cloneData;

        if ( clone && cloneItemId && itemTypeId ) {
            cloneData = global.data[ dataContext ]?.items[ cloneItemId ]?.toUpdateJSON();

            if ( metaModel[ schema ].getItemType( itemTypeId )?.isKeyAutogenerated ) {
                delete cloneData.key;
            }
        }

        const item : ItemAttributesForCreation | Item    = addExisting
                  ? selectedItem!
                  : clone && cloneItemId && itemTypeId
                      ? new Item( { itemTypeId }, dataContext, schema ).update( cloneData )
                      : {
                          itemTypeId: metaModel[ schema ].getItemType( selectedItemTypeName )!.id,
                          statusId
                      },
              selectedPayloadId                          = addExisting || clone ? ( item as Item ).id : 0,
              referenceDirection                         = AddTreeNodeDialog.getReferenceDirection( schema, selectedReferenceSetId, targetItemTypeName!, allowedReferenceSetIds ),
              reference : ReferenceAttributesForCreation = {
                  referenceSetId,
                  statusId,
                  srcItemId : referenceDirection === 'src' ? selectedPayloadId : targetItemId ?? 0,
                  destItemId: referenceDirection === 'src' ? targetItemId ?? 0 : selectedPayloadId
              };

        props.onAddNode( item, reference );

        props.onHide();
    }
};

const getAddItemDialog = ( props : ComponentProps ) => {
    const propsToUse : AllProps = {
              ...defaultProps,
              ...props
          },
          stateToUse : State    = {
              ...defaultState,
              statusId: metaModel[ props.schema ].getStatus( 'active' )!.id
          },
          derivedState : State  = {
              ...stateToUse,
              ...AddTreeNodeDialog.getDerivedStateFromProps( propsToUse, stateToUse )
          },
          {
              addMode
          }                     = propsToUse,
          {
              possibleItemTypes,
              possibleReferenceSets
          }                     = derivedState;

    let retVal : React.JSX.Element | null = null;

    if ( possibleItemTypes.length === 1 && possibleReferenceSets.length === 1 && addMode !== 'existing' ) {
        onCreateButtonClick( propsToUse, derivedState );
    }
    else {
        retVal = <AddTreeNodeDialog {...props} />;
    }

    return retVal;
};

/**
 * AddTreeNodeDialog
 */
export class AddTreeNodeDialog extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    static getReferenceDirection( schema : Schema, cirsId : number, targetItemTypeName : string, allowedReferenceSetIds? : {
        [ cirsId : number ] : string
    } ) {
        const referenceSetId = cirsId < 0 ? -cirsId : cirsId;

        return allowedReferenceSetIds?.[ referenceSetId ]
            ? allowedReferenceSetIds?.[ referenceSetId ] === 'dest'
                ? referenceSetId === cirsId
                    ? 'src'
                    : 'dest'
                : referenceSetId === cirsId
                    ? 'dest'
                    : 'src'
            : metaModel[ schema ].getReferenceSet( referenceSetId )?.destItemType.name === targetItemTypeName
                ? referenceSetId === cirsId
                    ? 'src'
                    : 'dest'
                : referenceSetId === cirsId
                    ? 'dest'
                    : 'src';
    }

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            ...defaultState,
            statusId: metaModel[ props.schema ].getStatus( 'active' )!.id
        };

        this.onItemTypeDropdownChange = this.onItemTypeDropdownChange.bind( this );
        this.onReferenceSetDropdownChange = this.onReferenceSetDropdownChange.bind( this );
        this.onGetReferenceSetLabel = this.onGetReferenceSetLabel.bind( this );
        this.onCreateButtonClick = this.onCreateButtonClick.bind( this );
        this.onCancelButtonClick = this.onCancelButtonClick.bind( this );
        this.onSearchItemDropdownChange = this.onSearchItemDropdownChange.bind( this );

        this.checkReference = throttle( this.checkReference, 200, { trailing: true } );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : ComponentProps, state : State ) : Partial<State> {
        const {
                  schema,
                  dataContext,
                  includeReferenceSets,
                  targetItemTypeName,
                  targetItemId,
                  cloneItemTypeName,
                  allowedReferenceSetIds
              }              = props,
              referenceSetId = state.selectedReferenceSetId < 0 ? -state.selectedReferenceSetId : state.selectedReferenceSetId;

        let retVal : Partial<State> = {};

        // selectedReferenceSetId changes only, if currently selected is not in allow list
        let selectedReferenceSetId = includeReferenceSets?.length === 1
            ? includeReferenceSets[ 0 ]
            : (
                includeReferenceSets?.includes( referenceSetId )
                    ? state.selectedReferenceSetId
                    : 0
            );

        // TODO: implement helper function to get referenceDirection
        const possibleItemTypes = _.uniq( ( includeReferenceSets ?? [] ).map( cirsId => {
            const referenceDirection = AddTreeNodeDialog.getReferenceDirection( schema, cirsId, targetItemTypeName!, allowedReferenceSetIds );
            return metaModel[ schema ].getReferenceSet( cirsId )?.[ referenceDirection === 'src' ? 'srcItemTypeId' : 'destItemTypeId' ] ?? 0
        } ).filter( ( citId : number ) => !cloneItemTypeName || cloneItemTypeName === metaModel[ schema ].getItemType( citId )?.name ) );
        const possibleItemTypeNames = possibleItemTypes.map( citId => metaModel[ schema ].getItemType( citId )?.name ?? '' );

        if ( possibleItemTypes && !_.isEqual( possibleItemTypes, state.possibleItemTypes ) ) {
            retVal = {
                ...retVal,
                possibleItemTypes
            }
        }

        const selectedItemTypeName =
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  cloneItemTypeName || (
                      possibleItemTypeNames.length === 1
                          ? possibleItemTypeNames[ 0 ]
                          : ( possibleItemTypeNames.includes( state.selectedItemTypeName ) ? state.selectedItemTypeName : '' )
                  );

        if ( selectedItemTypeName ) {
            const selectedItemTypeLabel = `<b>${getItemTypeDisplayName( selectedItemTypeName, schema )}</b>`;

            retVal = {
                ...retVal,
                selectedItemTypeName,
                selectedItemTypeLabel
            }
        }

        let possibleReferenceSets;
        if ( selectedItemTypeName && includeReferenceSets ) {
            possibleReferenceSets = includeReferenceSets.filter( cirsId => {
                const referenceDirection = AddTreeNodeDialog.getReferenceDirection( schema, cirsId, targetItemTypeName!, allowedReferenceSetIds );
                return metaModel[ schema ].getItemType( metaModel[ schema ].getReferenceSet( cirsId )?.[ referenceDirection === 'src' ? 'srcItemTypeId' : 'destItemTypeId' ] ?? 0 )?.name === selectedItemTypeName
            } );
            if ( possibleReferenceSets.length === 1 ) {
                selectedReferenceSetId = possibleReferenceSets[ 0 ];
            }
        }
        else {
            selectedReferenceSetId = 0;
            possibleReferenceSets = includeReferenceSets;
        }

        // check possible referenceSets for cardinality violations and filter them out
        if ( targetItemId && possibleReferenceSets ) {
            const targetItem = global.data[ dataContext ]?.items[ targetItemId ];
            possibleReferenceSets = possibleReferenceSets.filter( cirsId => {
                const referenceDirection = AddTreeNodeDialog.getReferenceDirection( schema, cirsId, targetItemTypeName!, allowedReferenceSetIds );
                return checkCardinalityFeOnly(
                    dataContext, schema, cirsId,
                    referenceDirection === 'src' ? undefined : targetItem,
                    referenceDirection === 'src' ? targetItem : undefined
                ).cardinalityCheck;
            } );
        }

        if ( selectedReferenceSetId !== state.selectedReferenceSetId ) {
            retVal = {
                ...retVal,
                selectedReferenceSetId
            }
        }

        if ( possibleReferenceSets !== state.possibleReferenceSets ) {
            retVal = {
                ...retVal,
                possibleReferenceSets
            }
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate( prevProps : ComponentProps, prevState : State ) {
        if ( prevState.selectedReferenceSetId !== this.state.selectedReferenceSetId || prevState.selectedItem !== this.state.selectedItem ) {
            this.checkReference();
        }

    }

    /**
     * Validates the chosen reference against database.
     */
    checkReference() {
        const {
                  uid,
                  dataContext,
                  targetItemId,
                  targetItemTypeName,
                  schema,
                  allowedReferenceSetIds
              }              = this.props,
              {
                  selectedReferenceSetId,
                  selectedItem
              }              = this.state,
              referenceSetId = selectedReferenceSetId < 0 ? -selectedReferenceSetId : selectedReferenceSetId;

        if ( referenceSetId && targetItemId ) {

            const targetItem = global.data[ dataContext ]?.items[ targetItemId ];
            const referenceDirection = AddTreeNodeDialog.getReferenceDirection( schema, referenceSetId, targetItemTypeName!, allowedReferenceSetIds );

            const doCheck = async () => {
                const cardinalityCheckResult = await checkCardinality(
                    uid, dataContext, schema, selectedReferenceSetId,
                    referenceDirection === 'src' ? selectedItem : targetItem,
                    referenceDirection === 'src' ? targetItem : selectedItem
                );
                this.setState( cardinalityCheckResult as State );
            };

            doCheck().catch( ( error ) => {
                console.log( error );
            } );
        }
    }

    /**
     * Handler for ItemTypeDropdown change event
     *
     * @private
     */
    onItemTypeDropdownChange( selectedItemTypeName : string ) {
        const { schema, includeReferenceSets, targetItemTypeName, allowedReferenceSetIds } = this.props;
        const itemTypeId = metaModel[ schema! ].getItemType( selectedItemTypeName )?.id ?? 0;
        const referenceSets = includeReferenceSets?.filter(
            cirsId => {
                const referenceDirection = AddTreeNodeDialog.getReferenceDirection( schema, cirsId, targetItemTypeName!, allowedReferenceSetIds );
                return metaModel[ schema! ].getReferenceSet( cirsId )?.[ referenceDirection === 'src' ? 'srcItemTypeId' : 'destItemTypeId' ] === itemTypeId
            }
        ) ?? [];
        const selectedReferenceSetId = referenceSets.length === 1 ? referenceSets[ 0 ] : 0;

        this.setState( {
            selectedItemTypeName,
            selectedReferenceSetId
        } );

        this.props.onInteraction?.();
    }

    /**
     * Handler for ReferenceSetDropdown change event
     *
     * @private
     */
    onReferenceSetDropdownChange( selectedReferenceSetId : number ) {
        const {
                  schema,
                  targetItemTypeName,
                  allowedReferenceSetIds
              }                   = this.props,
              referenceSetId      = selectedReferenceSetId < 0 ? -selectedReferenceSetId : selectedReferenceSetId,
              referenceDirection  = AddTreeNodeDialog.getReferenceDirection( schema, selectedReferenceSetId, targetItemTypeName!, allowedReferenceSetIds ),
              itemTypeIdAttribute = referenceDirection === 'src'
                  ? 'srcItemTypeId'
                  : 'destItemTypeId',
              itemTypeId          = metaModel[ schema ].getReferenceSet( referenceSetId )?.[ itemTypeIdAttribute ] ?? 0,
              itemTypeName        = metaModel[ schema ].getItemType( itemTypeId )?.name ?? '';

        this.setState( {
            selectedItemTypeName: itemTypeName,
            selectedReferenceSetId
        } );

        this.props.onInteraction?.();
    }

    onGetReferenceSetLabel( srcItemTypeName : string, srcItemTypeLabel : string, destItemTypeName : string, destItemTypeLabel : string, referenceTypeName : string, referenceTypeLabel : string ) : string | undefined {
        let retVal : string | undefined;

        if ( srcItemTypeName === 'YnItemType' ) {
            if ( referenceTypeLabel === 'isDestObjectType' ) {
                retVal = __( 'YN.base.itemTree.item.thisIsDest.title' );
            }
            else if ( referenceTypeLabel === 'isSourceObjectType' ) {
                retVal = __( 'YN.base.itemTree.item.thisIsSource.title' );
            }
        }
        else if ( destItemTypeName === 'YnItemType' ) {
            if ( referenceTypeLabel === 'isDestObjectType' ) {
                retVal = __( 'YN.base.itemTree.item.thisIsSource.title' );
            }
            else if ( referenceTypeLabel === 'isSourceObjectType' ) {
                retVal = __( 'YN.base.itemTree.item.thisIsDest.title' );
            }
        }

        return retVal;
    }

    /**
     * Handler for SearchItemDropdown change
     *
     * @private
     */
    onSearchItemDropdownChange( item : Item ) {
        this.setState( {
            selectedItem: item
        } );

        this.props.onInteraction?.();
    }

    /**
     * Handler for create Button click event
     *
     * @private
     */
    onCreateButtonClick( event : React.MouseEvent<HTMLButtonElement> ) {
        onCreateButtonClick( this.props, this.state );
        event.stopPropagation();
    }

    /**
     * Handler for cancel Button click event
     *
     * @private
     */
    onCancelButtonClick() {
        this.props.onHide();
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  rawMode,
                  showAdditionalRawNames,
                  targetItemTypeName,
                  cloneItemLabel,
                  addMode,
                  useThisLabel
              }                    = this.props,
              addExisting          = addMode === 'existing',
              {
                  cardinalityError,
                  cardinalityCheck,
                  selectedItemTypeName,
                  selectedItemTypeLabel,
                  possibleItemTypes,
                  selectedReferenceSetId,
                  possibleReferenceSets,
                  statusId,
                  selectedItem
              }                    = this.state,
              referenceSetId       = selectedReferenceSetId < 0 ? -selectedReferenceSetId : selectedReferenceSetId,
              referenceTypeName    = referenceSetId ? metaModel[ schema ].getReferenceSet( referenceSetId )?.referenceType.name ?? '' : '',
              referenceDirection   = referenceSetId
                  ? ( metaModel[ schema ].getReferenceSet( referenceSetId )?.srcItemType.name === selectedItemTypeName
                          ? selectedReferenceSetId === referenceSetId
                              ? 'src'
                              : 'dest'
                          : selectedReferenceSetId === referenceSetId
                              ? 'dest'
                              : 'src'
                  )
                  : 'dest',
              createButtonDisabled = addExisting ? !selectedItem || !cardinalityCheck : selectedItemTypeName === '' || referenceTypeName === '';

        return <Panel
            style={{ width: '25rem' }}
            pt={{
                content: {
                    style: {
                        border: 'none'
                    }
                }
            }}
        >
            <label
                key={`${uid}_header`}

                className="yn-menu-title-label"
            >
                {__(
                    addMode === 'existing' ? 'YN.base.itemTree.item.addExisting.title'
                        : addMode === 'clone' ? 'YN.base.itemTree.item.clone.title'
                            : 'YN.base.itemTree.item.add.title',
                    {
                        itemType: ( targetItemTypeName ? metaModel[ schema ].getItemType( targetItemTypeName )?.label : undefined ) ?? ''
                    }
                )}
            </label>

            <ItemTypeDropdown
                key={`${uid}_itemTypeDropdown`}

                uid={uid}

                className={!selectedItemTypeName ? 'yn-error' : ''}

                schema={schema}
                includeItemTypes={possibleItemTypes}
                rawMode={rawMode}
                showAdditionalRawNames={showAdditionalRawNames}
                permission="read"
                genericPermission="none"
                value={selectedItemTypeName}
                onChange={this.onItemTypeDropdownChange}
                label={__( 'YN.base.itemTree.item.itemType.label' )}
                style={{ width: '100%' }}
            />

            <ReferenceSetDropdown
                key={`${uid}_referenceSetDropdown`}

                uid={uid}

                className={!selectedReferenceSetId ? 'yn-error' : ''}

                schema={schema}
                itemTypeName1={selectedItemTypeName}
                itemTypeName2={targetItemTypeName}
                itemTypeLabel1={useThisLabel ? __( 'YN.base.finder.this.label' ) : selectedItemTypeLabel}
                referenceTypeName={referenceTypeName}
                referenceDirection={referenceDirection}
                includeReferenceSets={possibleReferenceSets}
                alwaysAllowSelect
                rawMode={rawMode}
                showAdditionalRawNames={showAdditionalRawNames}
                permission="read"
                genericPermission="none"
                value={selectedReferenceSetId}
                onChange={this.onReferenceSetDropdownChange}
                onGetReferenceSetLabel={this.onGetReferenceSetLabel}
                label={__( 'YN.base.itemTree.item.referenceSet.label' )}
                style={{ width: '100%' }}
            />

            {addExisting &&
                <SearchItemDropdown
                    key={`${uid}_searchItemDropdown`}

                    uid={uid}

                    schema={schema}
                    status={metaModel[ schema ].getStatus( statusId )!.name as Status}
                    itemTypeName={selectedItemTypeName}
                    itemLabel={cloneItemLabel}
                    value={selectedItem}
                    onChange={this.onSearchItemDropdownChange}
                    label={__( 'YN.base.itemTree.item.item.label' )}
                    style={{ width: '100%' }}
                />
            }

            {cardinalityError
                ? <>
                    <hr className="yn-error-ruler" style={{ clear: 'both' }}/>
                    <span className="yn-error">{cardinalityError}</span>
                </>
                : ''}


            <hr className="mt-3 mb-3 yn-ruler"/>

            <div className="flex justify-content-end">
                <Button
                    key={`${uid}_createButton`}

                    label={__( 'YN.base.common.create' )}
                    disabled={createButtonDisabled}
                    onClick={this.onCreateButtonClick}
                    className="p-button-outlined mr-3 yn-button-defaultwidth"
                />
                <Button
                    key={`${uid}_cancelButton`}

                    label={__( 'YN.base.common.cancel' )}
                    onClick={this.onCancelButtonClick}
                    className="p-button-secondary p-button-outlined yn-button-defaultwidth"
                />
            </div>
        </Panel>;
    }

}


export default getAddItemDialog;
